import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { Avatar, Tooltip } from "@fluentui/react-components";
import moment from "moment";
import { Activity, ChevronRight } from "lucide-react";
import styles from "../ResourcesPlanner.module.scss";
import HoverCard from "../../../components/hoverCard/HoverCard";
import CustomPopover from "../../../components/popover/Popover";
import CustomButton from "../../../components/controls/button/Button";
import { ArrowDownMedium, PersonPurpleIcon } from "../../../modules/default-svg";
import { TimelineTaskIcons } from "../../../helpers/utils";
import DateField from "../../../components/controls/dateField/DateField";
import TasksPill from "./TasksPill";
import ResourceAllocationPopover from "./ResourceAllocationPopover";
import { periodCapacity, resourceAssignment, resourceHours, round2 } from "../engine/aggregates";
import { dayToIso } from "../engine/dayMath";
import { RowType } from "../engine/rowModel";
import { projectSpan } from "../engine/summaryRange";
import { LEFT_PANEL_MAX_WIDTH, LEFT_PANEL_MIN_WIDTH, LOADING_MORE_HEIGHT } from "../constants";
import { useData, usePlannerEngine, useRowModel, useSummaryRange, useSummaryReady, useUi, useView, useViewport } from "../plannerContext";

const PERIOD_OPTIONS = [
  { value: "last_week", label: "Last week" },
  { value: "this_week", label: "This week" },
  { value: "next_week", label: "Next week" },
  { value: "last_month", label: "Last month" },
  { value: "this_month", label: "This month" },
  { value: "next_month", label: "Next month" },
];

const statusIcon = (status) => TimelineTaskIcons[status]?.();

/**
 * The hours of a resource for the summary period, or null while hours are hidden (nothing is computed then) or while
 * the resource has not yet got everything it has in a long period, which `isLoading` says.
 */
function useHours(resourceId) {
  const allocs = useData((state) => state.data.allocs[resourceId]);
  const showHours = useView((state) => state.showHours);
  const range = useSummaryRange();
  const isReady = useSummaryReady(resourceId);
  const hours = useMemo(() => (showHours && allocs && isReady ? resourceHours(allocs, range.startDay, range.endDay) : null), [showHours, allocs, isReady, range]);

  return { allocs, hours, range, showHours, isLoading: showHours && !isReady };
}

/** A pill with a number in hours or as a percentage, inside the popover that explains it; dots while the number is on its way. */
function Pill({ over, loading, children }) {
  if (loading) return <span className={`${styles.allocationPill} ${styles.pillNormal} ${styles.pillLoading}`} aria-busy="true">…</span>;
  return <span className={`${styles.allocationPill} ${over ? styles.pillOver : styles.pillNormal}`}>{children}</span>;
}

/** The row of the panel that a row of the timeline lines up with: same offset, same height. */
function RowShell({ row, top, entering, className, onClick, children }) {
  return (
    <div
      className={`${styles.leftPanelRow} ${styles.leftRow} ${className || ""} ${entering ? styles.rowEnter : ""}`}
      style={{ height: row.height, transform: `translateY(${top}px)` }}
      onClick={onClick}
    >
      {children}
    </div>
  );
}

/** The right-hand end of a row: the hours, when they are shown, and the number of tasks the row stands for in the period. */
function RowEnd({ row, label, children }) {
  return (
    <div className={styles.rowEnd}>
      {children}
      <TasksPill row={row} label={label} />
    </div>
  );
}

function Chevron({ row, size }) {
  return (
    <span className={`${styles.expandIcon} ${!row.expandable ? "invisible" : ""}`}>
      <ChevronRight size={size} className={`${styles.chevron} ${row.expanded ? styles.chevronOpen : ""}`} />
    </span>
  );
}

function ResourceRow({ row, top, entering }) {
  const { actions } = usePlannerEngine();
  const resource = useData((state) => state.data.resources[row.resourceId]);
  const projects = useData((state) => state.data.projects);
  const displayMode = useView((state) => state.displayMode);
  const isInsightsOpen = useUi((state) => state.resourceInsights?.id === row.resourceId);
  const { hours, range, showHours, isLoading } = useHours(row.resourceId);

  const popoverResource = useMemo(
    () => ({
      ...resource,
      projects: resource.memberships.map((membership) => ({ id: membership.projectId, name: projects[membership.projectId]?.name, maxWeeklyHours: membership.maxWeeklyHours })),
    }),
    [resource, projects]
  );

  const assignment = resourceAssignment(resource);
  const allocated = hours ? round2(hours.total) : 0;
  const resourceCap = periodCapacity(resource.maxWeeklyHours, range.startDay, range.endDay);
  const assignmentCap = periodCapacity(assignment.totalAssignmentWeekly, range.startDay, range.endDay);
  const isOver = showHours && (assignment.isOverAssigned || allocated > resourceCap);
  const utilization = assignmentCap > 0 ? Math.round((allocated / assignmentCap) * 100) : 0;

  return (
    <RowShell
      row={row}
      top={top}
      entering={entering}
      className={`${styles.leftPanelRowResource} ${isOver ? styles.leftPanelRowResourceOver : ""}`}
      onClick={() => row.expandable && actions.toggleExpand(row.resourceId, row.key)}
    >
      <Chevron row={row} size={14} />

      {/* In front of the picture, so it is in the same place on every resource whatever the length of the name. */}
      <Tooltip content="Resource Insights" relationship="label" positioning="above">
        <button
          type="button"
          className={`${styles.insightsBtn} ${isInsightsOpen ? styles.insightsBtnActive : ""}`}
          onClick={(event) => {
            event.stopPropagation();
            actions.openResourceInsights(resource);
          }}
        >
          <Activity size={13} />
        </button>
      </Tooltip>

      <HoverCard
        HoverCardTrigger={
          <div>
            <Avatar size={28} color="colorful" name={resource.name} image={{ src: resource.image || null }} />
          </div>
        }
        AvatarId={resource.id}
        AvatarName={resource.name}
        AvatarImage={resource.image}
        AvatarDesignation={resource.title}
        AvatarEmail={resource.email}
        AvatarPhone={resource.phoneNumber}
        AvatarIsExternal={resource.isExternal}
        CanSendMessage={true}
      />
      <div className={styles.resourceInfo}>
        <div className={styles.resourceName}>
          <span className={styles.resourceNameText}>{resource.name}</span>
          {resource.title && <span className={styles.resourceTitleText}>{resource.title}</span>}
        </div>
        <RowEnd row={row} label={resource.name}>
          {showHours && (
            <div className={styles.resourceAllocationPill}>
              <ResourceAllocationPopover loading={isLoading} level="resource" resource={popoverResource} data={{ ...assignment, allocated, assignmentCap, resourceCap }}>
                <Pill loading={isLoading} over={isOver}>{displayMode === "hours" ? `${allocated} / ${assignmentCap} hrs` : `${utilization}%`}</Pill>
              </ResourceAllocationPopover>
            </div>
          )}
        </RowEnd>
      </div>
    </RowShell>
  );
}

function ProjectRow({ row, top, entering }) {
  const { actions } = usePlannerEngine();
  const resource = useData((state) => state.data.resources[row.resourceId]);
  const project = useData((state) => state.data.projects[row.projectId]);
  const scopes = useData((state) => state.data.scopes);
  const displayMode = useView((state) => state.displayMode);
  const { hours, range, showHours, isLoading } = useHours(row.resourceId);

  const membership = resource.memberships.find((candidate) => candidate.projectId === row.projectId);
  const projectWeekly = membership?.maxWeeklyHours || 0;
  const allocated = hours ? round2(hours.byProject[row.projectId] || 0) : 0;
  const cap = periodCapacity(projectWeekly, range.startDay, range.endDay);
  const isOverAssigned = projectWeekly > resource.maxWeeklyHours;
  const isOver = isOverAssigned || allocated > cap;

  const scopeBreakdown = useMemo(
    () => (membership?.scopeIds || []).map((id) => scopes[id]).filter(Boolean).map((scope) => ({ id: scope.id, name: scope.name, allocated: hours ? round2(hours.byScope[scope.id] || 0) : 0 })),
    [membership, scopes, hours]
  );

  return (
    <RowShell row={row} top={top} entering={entering} className={styles.leftPanelRowProject} onClick={() => row.expandable && actions.toggleExpand(row.resourceId, row.key)}>
      <Chevron row={row} size={12} />
      <div className="flex-shrink-0 w-3 h-3 flex items-center justify-center mr-1">{statusIcon(project?.status)}</div>
      <div className={styles.resourceInfo}>
        <div className={styles.resourceName}>
          <span className={styles.resourceNameText}>{project?.name}</span>
        </div>
        <RowEnd row={row} label={project?.name}>
          {showHours && (
            <div className={styles.resourceAllocationPill}>
              <ResourceAllocationPopover
                loading={isLoading}
                level="project"
                resource={resource}
                project={project}
                data={{ projAllocated: allocated, projCap: cap, isProjectOverAssigned: isOverAssigned, projectMaxWeekly: projectWeekly, resourceMaxWeekly: resource.maxWeeklyHours, scopes: scopeBreakdown }}
              >
                <Pill loading={isLoading} over={isOver}>{displayMode === "hours" ? `${allocated}h / ${cap}h` : `${cap > 0 ? Math.round((allocated / cap) * 100) : 0}%`}</Pill>
              </ResourceAllocationPopover>
            </div>
          )}
        </RowEnd>
      </div>
    </RowShell>
  );
}

/** The tasks of a scope or group as the allocation popover lists them. */
function useTaskList(allocs, hours, filter) {
  return useMemo(() => {
    if (!allocs) return [];
    return [...allocs.dated, ...allocs.undated].filter(filter).map((allocation) => ({
      id: allocation.taskId,
      name: allocation.name,
      status: allocation.status,
      allocation: { startDate: allocation.startDate },
      allocated: hours ? round2(hours.byTask[allocation.taskId] || 0) : 0,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allocs?.dated, allocs?.undated, hours]);
}

function ScopeRow({ row, top, entering }) {
  const { actions } = usePlannerEngine();
  const resource = useData((state) => state.data.resources[row.resourceId]);
  const project = useData((state) => state.data.projects[row.projectId]);
  const scope = useData((state) => state.data.scopes[row.scopeId]);
  const { allocs, hours, range, showHours, isLoading } = useHours(row.resourceId);

  const projectWeekly = resource.memberships.find((membership) => membership.projectId === row.projectId)?.maxWeeklyHours || 0;
  const projectCap = periodCapacity(projectWeekly, range.startDay, range.endDay);
  const allocated = hours ? round2(hours.byScope[row.scopeId] || 0) : 0;
  const hasTaskOver = Boolean(hours) && projectCap > 0 && round2(hours.maxTaskInScope[row.scopeId] || 0) > projectCap;

  const ungroupedFilter = useCallback((allocation) => allocation.scopeId === row.scopeId && !allocation.groupId, [row.scopeId]);
  const tasks = useTaskList(allocs, hours, ungroupedFilter);

  return (
    <RowShell row={row} top={top} entering={entering} className={styles.leftPanelRowScope} onClick={() => row.expandable && actions.toggleExpand(row.resourceId, row.key)}>
      <Chevron row={row} size={11} />
      <div className="flex-shrink-0 w-3 h-3 flex items-center justify-center mr-1">{statusIcon(scope?.status)}</div>
      <div className={styles.resourceInfo}>
        <div className={styles.resourceName}>
          <span className={styles.resourceNameText}>{scope?.name}</span>
        </div>
        <RowEnd row={row} label={scope?.name}>
          {showHours && (
            <div className={styles.resourceAllocationPill}>
              <ResourceAllocationPopover
                loading={isLoading}
                level="scope"
                resource={resource}
                project={project}
                scope={scope}
                data={{ scopeAllocated: allocated, projectMaxWeekly: projectWeekly, projCap: projectCap, tasks }}
              >
                <Pill loading={isLoading} over={hasTaskOver}>{allocated}h</Pill>
              </ResourceAllocationPopover>
            </div>
          )}
        </RowEnd>
      </div>
    </RowShell>
  );
}

function GroupRow({ row, top, entering }) {
  const { actions } = usePlannerEngine();
  const resource = useData((state) => state.data.resources[row.resourceId]);
  const project = useData((state) => state.data.projects[row.projectId]);
  const scope = useData((state) => state.data.scopes[row.scopeId]);
  const group = useData((state) => state.data.groups[row.groupId]);
  const { allocs, hours, range, showHours, isLoading } = useHours(row.resourceId);

  const projectWeekly = resource.memberships.find((membership) => membership.projectId === row.projectId)?.maxWeeklyHours || 0;
  const projectCap = periodCapacity(projectWeekly, range.startDay, range.endDay);
  const allocated = hours ? round2(hours.byGroup[row.groupId] || 0) : 0;
  const hasTaskOver = Boolean(hours) && projectCap > 0 && round2(hours.maxTaskInGroup[row.groupId] || 0) > projectCap;

  const groupFilter = useCallback((allocation) => allocation.groupId === row.groupId, [row.groupId]);
  const tasks = useTaskList(allocs, hours, groupFilter);

  return (
    <RowShell row={row} top={top} entering={entering} className={styles.leftPanelRowTaskGroup} onClick={() => row.expandable && actions.toggleExpand(row.resourceId, row.key)}>
      <Chevron row={row} size={11} />
      <div className="flex-shrink-0 w-3 h-3 flex items-center justify-center mr-1">{statusIcon(group?.status)}</div>
      <div className={styles.resourceInfo}>
        <div className={styles.resourceName}>
          <span className={styles.resourceNameText}>{group?.name}</span>
        </div>
        <RowEnd row={row} label={group?.name}>
          {showHours && (
            <div className={styles.resourceAllocationPill}>
              <ResourceAllocationPopover
                loading={isLoading}
                level="taskGroup"
                resource={resource}
                project={project}
                scope={scope}
                group={group}
                data={{ groupAllocated: allocated, projectMaxWeekly: projectWeekly, projCap: projectCap, tasks }}
              >
                <Pill loading={isLoading} over={hasTaskOver}>{allocated}h</Pill>
              </ResourceAllocationPopover>
            </div>
          )}
        </RowEnd>
      </div>
    </RowShell>
  );
}

function TaskRow({ row, top, entering }) {
  const { actions } = usePlannerEngine();
  const resource = useData((state) => state.data.resources[row.resourceId]);
  const project = useData((state) => state.data.projects[row.projectId]);
  const scope = useData((state) => state.data.scopes[row.scopeId]);
  const { hours, range, showHours, isLoading } = useHours(row.resourceId);

  const { allocation } = row;
  const isDated = allocation.startDay !== null;
  const projectWeekly = resource.memberships.find((membership) => membership.projectId === row.projectId)?.maxWeeklyHours || 0;
  const projectCap = periodCapacity(projectWeekly, range.startDay, range.endDay);
  const allocated = hours ? round2(hours.byTask[allocation.taskId] || 0) : 0;

  const task = useMemo(
    () => ({ id: allocation.taskId, name: allocation.name, status: allocation.status, allocation: { startDate: allocation.startDate, endDate: allocation.endDate, hoursPerDay: allocation.hoursPerDay } }),
    [allocation]
  );

  return (
    <RowShell
      row={row}
      top={top}
      entering={entering}
      className={`${styles.leftPanelRowTask} ${row.groupId ? styles.leftPanelRowTaskGrouped : ""}`}
      onClick={() => isDated && actions.goToDate(allocation.startDate)}
    >
      <div className="flex-shrink-0 w-3 h-3 flex items-center justify-center mr-1">{statusIcon(allocation.status)}</div>
      <div className={styles.resourceInfo}>
        <div className={styles.resourceName}>
          <span className={styles.resourceNameText}>{allocation.name}</span>
        </div>
        {(showHours || !isDated) && (
          <div className={styles.resourceAllocationPill}>
            {isDated ? (
              <ResourceAllocationPopover loading={isLoading} level="task" resource={resource} project={project} scope={scope} task={task} data={{ taskAllocated: allocated, projectMaxWeekly: projectWeekly, projCap: projectCap }}>
                <Pill loading={isLoading} over={projectCap > 0 && allocated > projectCap}>{allocated}h</Pill>
              </ResourceAllocationPopover>
            ) : (
              <span className={styles.noDateBadge}>No dates</span>
            )}
          </div>
        )}
      </div>
    </RowShell>
  );
}

const ROW_COMPONENTS = {
  [RowType.RESOURCE]: ResourceRow,
  [RowType.PROJECT]: ProjectRow,
  [RowType.SCOPE]: ScopeRow,
  [RowType.GROUP]: GroupRow,
  [RowType.TASK]: TaskRow,
};

const MemoRows = Object.fromEntries(Object.entries(ROW_COMPONENTS).map(([type, Component]) => [type, React.memo(Component)]));

/** The rows of the panel that are on screen. */
function LeftRows() {
  const model = useRowModel();
  const enteringPrefix = useView((state) => state.enteringPrefix);
  const firstRow = useViewport((state) => state.firstRow);
  const lastRow = useViewport((state) => state.lastRow);

  const rows = [];
  for (let i = Math.max(0, firstRow); i <= lastRow && i < model.rows.length; i += 1) {
    const row = model.rows[i];
    const Component = MemoRows[row.type];
    rows.push(<Component key={row.key} row={row} top={model.tops[i]} entering={Boolean(enteringPrefix) && row.key.startsWith(`${enteringPrefix}/`)} />);
  }
  return <>{rows}</>;
}

/** "5 Oct – 18 Oct", with the year added to a day that is not in the current year, or to both when `withYears`. */
function shortRange(startDay, endDay, today, withYears = false) {
  const year = moment(dayToIso(today)).year();
  const write = (day) => {
    const date = moment(dayToIso(day));
    return date.format(withYears || date.year() !== year ? "D MMM YY" : "D MMM");
  };
  return `${write(startDay)} – ${write(endDay)}`;
}

/**
 * Chooses the period the hours, the number of tasks of each row and Resource Insights refer to: one of the usual
 * periods, the days the user picks, or, with a project selected, the whole life of that project.
 */
function PeriodDropdown() {
  const { actions, today } = usePlannerEngine();
  const summaryPeriod = useView((state) => state.summaryPeriod);
  const projectId = useUi((state) => state.projectFilter?.id);
  const project = useData((state) => (projectId ? state.data.projects[projectId] : null));
  const range = useSummaryRange();
  const [open, setOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(false);
  const [from, setFrom] = React.useState(null);
  const [to, setTo] = React.useState(null);

  const isCustom = summaryPeriod === "custom";
  const isOverall = summaryPeriod === "overall";
  const span = useMemo(() => projectSpan(project), [project]);
  const label = isCustom ? shortRange(range.startDay, range.endDay, today) : isOverall ? "Overall" : PERIOD_OPTIONS.find((option) => option.value === summaryPeriod)?.label;
  const canApply = Boolean(from && to && from <= to);

  const close = () => {
    setOpen(false);
    setEditing(false);
  };

  // The dates start out as the period that is in use.
  const startCustom = () => {
    setFrom(dayToIso(range.startDay));
    setTo(dayToIso(range.endDay));
    setEditing(true);
  };

  const applyCustom = () => {
    actions.setCustomPeriod(from, to);
    close();
  };

  return (
    <div className={styles.periodDropdown}>
      <CustomPopover
        icon={
          <CustomButton
            label={label}
            btnClass={`${styles.periodDropdownOption} d_btn `}
            rightIcon={
              <span className="ms-6 d-inline-flex">
                <ArrowDownMedium />
              </span>
            }
            clickHandler={() => {
              setOpen(!open);
              setEditing(false);
            }}
          />
        }
        width={editing ? "240px" : span ? "190px" : "140px"}
        PopSurfaceClass={styles.periodDropdownPopover}
        status={open}
      >
        {editing ? (
          <div className={styles.customRange}>
            {/* The buttons are above the dates, so the calendar of a date field never covers them. */}
            <div className={styles.customRangeHeader}>
              <h5>Custom range</h5>
              <button type="button" onClick={close}>
                Cancel
              </button>
              <button type="button" className={styles.customRangeApply} disabled={!canApply} onClick={applyCustom}>
                Apply
              </button>
            </div>
            <div className={styles.customRangeFields}>
              <label>
                From
                <DateField tooltip="From" className={styles.customRangeDateField} value={from} maxDate={to} onChange={setFrom} />
              </label>
              <label>
                To
                <DateField tooltip="To" className={styles.customRangeDateField} value={to} minDate={from} onChange={setTo} />
              </label>
            </div>
          </div>
        ) : (
          <>
            {PERIOD_OPTIONS.map((option) => (
              <div
                key={option.value}
                className={styles.periodDropdownPopoverItem}
                onClick={() => {
                  actions.setSummaryPeriod(option.value);
                  close();
                }}
              >
                {option.label}
              </div>
            ))}
            {span && (
              <div
                className={`${styles.periodDropdownPopoverItem} ${styles.periodDropdownPopoverItemCustom} ${isOverall ? styles.periodDropdownPopoverItemSelected : ""}`}
                onClick={() => {
                  actions.setSummaryPeriod("overall");
                  close();
                }}
              >
                <div className={styles.periodDropdownPopoverItemBody}>
                  <span>Overall</span>
                  <small>{shortRange(span.startDay, span.endDay, today, true)}</small>
                </div>
              </div>
            )}
            <div className={`${styles.periodDropdownPopoverItem} ${span ? "" : styles.periodDropdownPopoverItemCustom} ${isCustom ? styles.periodDropdownPopoverItemSelected : ""}`} onClick={startCustom}>
              Custom
            </div>
          </>
        )}
      </CustomPopover>
    </div>
  );
}

/**
 * The left panel: a header with the display and period controls, and the rows with each resource's name, its
 * hours and the nodes beneath it. The width can be dragged and is remembered. The panel is sticky, so it stays in
 * place while the timeline scrolls sideways.
 */
export default function PlannerLeftPanel() {
  const { actions } = usePlannerEngine();
  const model = useRowModel();
  const showHours = useView((state) => state.showHours);
  const displayMode = useView((state) => state.displayMode);
  const panelWidth = useView((state) => state.panelWidth);
  const isLoadingMore = useData((state) => state.isLoadingMore);
  const animating = useView((state) => Boolean(state.enteringPrefix));

  const panelRef = useRef(null);
  const resizing = useRef(null);

  // While the handle is dragged the width is written straight to the element; it is committed when the drag ends.
  useEffect(() => {
    const onMove = (event) => {
      if (!resizing.current || !panelRef.current) return;
      const width = Math.min(LEFT_PANEL_MAX_WIDTH, Math.max(LEFT_PANEL_MIN_WIDTH, resizing.current.width + event.clientX - resizing.current.x));
      resizing.current.latest = width;
      panelRef.current.style.width = `${width}px`;
      panelRef.current.style.minWidth = `${width}px`;
      panelRef.current.style.maxWidth = `${width}px`;
    };
    const onUp = () => {
      if (!resizing.current) return;
      const { latest } = resizing.current;
      resizing.current = null;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      actions.setPanelWidth(latest);
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [actions]);

  const startResize = (event) => {
    event.preventDefault();
    resizing.current = { x: event.clientX, width: panelRef.current.offsetWidth, latest: panelRef.current.offsetWidth };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  const height = model.totalHeight + (isLoadingMore ? LOADING_MORE_HEIGHT : 0);

  return (
    <div ref={panelRef} className={`${styles.leftPanel} ${animating ? styles.animateRows : ""}`} style={{ width: panelWidth, minWidth: panelWidth, maxWidth: panelWidth }}>
      <div className={styles.leftPanelResizeHandle} onMouseDown={startResize} />
      <div className={styles.leftPanelHeader}>
        <PersonPurpleIcon />
        {showHours && (
          <div className={styles.displayToggle}>
            <button type="button" className={`${styles.toggleBtn} ${displayMode === "hours" ? styles.toggleBtnActive : ""}`} onClick={() => actions.setDisplayMode("hours")}>
              hrs
            </button>
            <button type="button" className={`${styles.toggleBtn} ${displayMode === "percentage" ? styles.toggleBtnActive : ""}`} onClick={() => actions.setDisplayMode("percentage")}>
              %
            </button>
          </div>
        )}
        <PeriodDropdown />
      </div>

      <div className={styles.leftBody} style={{ height }}>
        <LeftRows />

        {isLoadingMore && (
          <div className={styles.skeletonStrip} style={{ top: model.totalHeight }}>
            {[0, 1].map((index) => (
              <div key={index} className={styles.skeletonRow}>
                <div className={styles.skeletonAvatar} />
                <div className={styles.skeletonLines}>
                  <div className={`${styles.skeletonLine} ${styles.skeletonLineName}`} />
                  <div className={`${styles.skeletonLine} ${styles.skeletonLinePill}`} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
